import { ID } from '../../../types/UtilityTypes'
import { concatAll, filter, map, mergeMap } from 'rxjs/operators'
import { isActionOf } from 'typesafe-actions'
import { objectActions } from './object-actions'
import { apiRequests } from '../../../http/config'
import {
    errorToast,
    infoToast,
    pushErrorAndEmpty,
    toastFailedResponse
} from '../../../components/controls/Toaster/Toaster'
import { projectActions } from '../project/project-actions'
import { objectsSelector, objectTasksSelector, pipeSelectors, projectObjectsSelector } from '../../../utils/selectors'
import { EMPTY, from, of } from 'rxjs'
import { matchPath } from 'react-router'
import { ROUTES } from '../../../http/routes'
import { push } from 'connected-react-router'
import { combineEpics } from 'redux-observable'
import { RootEpic } from '../../root'
import { ObjectEditState } from './object-reducer'
import { nextObjectName } from '../../../components/data-components/pickers/ObjectPicker/ObjectPicker'
import * as A from 'fp-ts/Array'
import { pipe } from 'fp-ts/function'
import { eqString } from 'fp-ts/Eq'

const create: RootEpic = action$ => action$.pipe(
    filter(isActionOf(objectActions.create.request)),
    mergeMap(action => apiRequests.workObject.add({ data: action.payload }).pipe(
        mergeMap(res => res.response.success ?
            of(apiRequests.project.update({
                params: { projectId: action.payload.projectId },
                data: { project_work_objects: { add: [res.response.row.id] } }
            })) :
            pushErrorAndEmpty(res.response, 'Не удалось создать объект')
        ),
        concatAll(),
        mergeMap(res => res.response.success ?
            from([
                objectActions.create.success(),
                projectActions.update.success(res.response.row, 'user')
            ]) :
            of(objectActions.create.failure(toastFailedResponse(res.response, 'Не удалось добавить объект к проекту')))
        )
    )),
)

const remove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(objectActions.remove.request)),
    mergeMap(() => ObjectEditState.match({
        delete: ({object}) => apiRequests.project.update({
            params: { projectId: object.project_id },
            data: {
                project_work_objects: {
                    delete: [object.id],
                }
            }
        }).pipe(
            mergeMap(res => res.response.success ?
                from([
                    objectActions.remove.success(object.id),
                    objectActions.cancelEdit(),
                ]) :
                of(objectActions.remove.failure(toastFailedResponse(res.response, 'Не удалось удалить объект'))),
            ),
        )
    }, () => EMPTY)(state$.value.data.object.editState)),
)
const update: RootEpic = action$ => action$.pipe(
    filter(isActionOf(objectActions.update.request)),
    mergeMap(({ payload }) => apiRequests.workObject.update({
        data: payload.changes,
        params: { objectId: payload.objectId },
    })),
    map(({ response }) => response.success ?
        objectActions.update.success(response.row, 'user') :
        objectActions.update.failure(toastFailedResponse(response, 'Не удалось обновить объект'))
    ),
)

const close: RootEpic = action$ => action$.pipe(
    filter(isActionOf(objectActions.close.request)),
    mergeMap(action => apiRequests.workObject.close({
        params: { objectId: action.payload.id },
        data: action.payload,
    })),
    map(res => res.response.success?
        objectActions.close.success(res.response.row):
        objectActions.close.failure(toastFailedResponse(res.response, 'Не удалось закрыть объект')),
    ),
)

const redirect: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(objectActions.remove.success)),
    mergeMap(action => {
        const taskPage = matchPath<{ taskId: string }>(location.pathname, { path: ROUTES.TASK })
        const tasks = objectTasksSelector(action.payload)(state$.value)
        if (taskPage && tasks.find(id => id == taskPage.params.taskId)) {
            infoToast('Объект удален. Возврат на страницу задач')
            return of(push(ROUTES.TASKS))
        }
        return EMPTY
    })
)
const projectRemove: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(projectActions.remove.success)),
    mergeMap(action => {
        const objectIds: ID[] = projectObjectsSelector(action.payload)(state$.value)
        return from(objectIds.map(objectActions.remove.success))
    }),
)

const duplicate: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(objectActions.duplicate)),
    map(action => {
        const objects = pipeSelectors(action.payload.project_id, projectObjectsSelector, objectsSelector)(state$.value)
        return objectActions.create.request({
            projectId: action.payload.project_id,
            work_object_type_id: action.payload.work_object_type.id,
            name: nextObjectName(action.payload.name ?? '', action.payload.work_object_type.work_object, objects),
        })
    })
)

const rename: RootEpic = (action$, state$) => action$.pipe(
    filter(isActionOf(objectActions.rename)),
    mergeMap(({payload: {object, name}}) => {
        const editedObject = object
        const exist = pipe(
            state$.value,
            pipeSelectors(object.project_id, projectObjectsSelector, objectsSelector),
            A.filter(obj => obj.work_object_type.id === editedObject.work_object_type.id && obj.id !== editedObject.id),
            A.map(object => object.name ?? ''),
            A.elem(eqString)(name)
        )
        if (exist) {
            errorToast(`Объект с именем "${name}" уже существует в проекте`)
            return EMPTY
        } else {
            return of(objectActions.update.request({
                objectId: object.id,
                changes: {name}
            }))
        }
    }),
)

export const objectEpic = combineEpics(create, remove, update, close, redirect, projectRemove, duplicate, rename)